import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { apiPost, readToken } from '../api/client.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { EmailRecipients } from '../components/EmailRecipients.jsx';
import { ChipInput } from '../components/ChipInput.jsx';
import { BrandColorField } from '../components/BrandColorField.jsx';
import { darken } from '../utils/color.js';
import { GOAL_OPTIONS, DEFAULT_BRAND_COLOR } from '../constants/brand.js';

/**
 * New Project wizard (ADR-0011). Two screens in one page:
 *   1. URL input → click Analyze → 5-15s spinner
 *   2. Confirm form (editable fields the AI extracted) → Save Project → Promote
 *
 * Saves as DRAFT first, then promotes to ACTIVE which fires Plan v1
 * generation and enables the seeded pillars.
 */
export default function NewProject() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [url, setUrl] = useState('');
  const [extracted, setExtracted] = useState(null);
  const [sourceUrl, setSourceUrl] = useState(null);

  // Editable confirm-form state
  const [form, setForm] = useState({
    key: '', display_name: '', ig_handle: '', positioning: '',
    audience: '', voice: '', features: '', hard_constraints: '',
    // Your input (ADR-0027) — optional brand-brief fields
    mission: '', primary_goal: '', current_focus: '',
    custom_instructions: '', topics_emphasize: [], topics_avoid: [],
    // Brand basics the analyzer can't infer
    brand_color: DEFAULT_BRAND_COLOR, recipient_emails: [],
  });

  // Pre-fill notification recipients with the signed-in user's email once it loads.
  useEffect(() => {
    if (user?.email) {
      setForm((f) => (f.recipient_emails.length ? f
        : { ...f, recipient_emails: [{ email: user.email, label: '', enabled: true }] }));
    }
  }, [user]);

  // Shared by BOTH intakes (URL analysis and brand-document upload) so the
  // confirm step is identical regardless of where the profile came from.
  const applyAnalysis = (data) => {
      const e = data.extracted || {};
      setExtracted(e);
      setSourceUrl(data.source_url || data.source_file || null);
      // AI basics refresh on (re-)analyze; the deeper brief suggestions FILL
      // EMPTY fields only, so a Re-analyze never clobbers the owner's edits.
      setForm((prev) => ({
        ...prev,
        key: prev.key || e.suggested_key || slugify(e.display_name || ''),
        display_name: e.display_name || prev.display_name,
        ig_handle: e.ig_handle || prev.ig_handle,
        positioning: e.positioning || prev.positioning,
        audience: e.audience || prev.audience,
        voice: e.voice || prev.voice,
        features: (e.features || []).join('\n') || prev.features,
        hard_constraints: (e.hard_constraints || []).join('\n') || prev.hard_constraints,
        mission: prev.mission || e.mission || '',
        current_focus: prev.current_focus || e.current_focus || '',
        primary_goal: prev.primary_goal || e.primary_goal || '',
        custom_instructions: prev.custom_instructions || e.custom_instructions || '',
        topics_emphasize: prev.topics_emphasize?.length ? prev.topics_emphasize : (e.topics_emphasize || []),
        topics_avoid: prev.topics_avoid?.length ? prev.topics_avoid : (e.topics_avoid || []),
      }));
  };

  const analyzeM = useMutation({
    mutationFn: (rawUrl) => apiPost('/projects/analyze', { url: rawUrl }),
    onSuccess: applyAnalysis,
  });

  // No-website intake (task #38): raw file bytes → same analysis pipeline.
  const analyzeFileM = useMutation({
    mutationFn: async (file) => {
      const res = await fetch('/api/projects/analyze-file', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${readToken()}`,
          'Content-Type': file.type || 'application/octet-stream',
          'X-Brand-Filename': encodeURIComponent(file.name),
        },
        body: file,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(body.detail || body.error || `HTTP ${res.status}`);
        err.body = body;
        throw err;
      }
      return body;
    },
    onSuccess: applyAnalysis,
  });

  const saveM = useMutation({
    mutationFn: () =>
      apiPost('/projects', {
        key: form.key,
        display_name: form.display_name,
        ig_handle: form.ig_handle || null,
        positioning: form.positioning,
        audience: form.audience,
        voice: form.voice,
        features: form.features.split('\n').map((s) => s.trim()).filter(Boolean),
        hard_constraints: form.hard_constraints.split('\n').map((s) => s.trim()).filter(Boolean),
        source_url: sourceUrl,
        // Your input (ADR-0027) — all optional
        mission: form.mission.trim() || null,
        primary_goal: form.primary_goal || null,
        current_focus: form.current_focus.trim() || null,
        custom_instructions: form.custom_instructions.trim() || null,
        topics_emphasize: form.topics_emphasize,
        topics_avoid: form.topics_avoid,
        brand_colors: { primary: form.brand_color, primary_dark: darken(form.brand_color) },
        recipient_emails: form.recipient_emails,
      }),
    onSuccess: () => {
      // Draft created — guard against a second create if promote later fails.
      setSaved(true);
      promoteM.mutate();
    },
  });

  const promoteM = useMutation({
    mutationFn: () => apiPost(`/projects/${encodeURIComponent(form.key)}/promote`),
    onSuccess: () => {
      navigate(`/projects/${encodeURIComponent(form.key)}?welcome=true`);
    },
  });

  const submitting = saveM.isPending || promoteM.isPending;
  const [saved, setSaved] = useState(false);          // draft persisted — only promote on retry
  const [confirmArmed, setConfirmArmed] = useState(false);

  const onConfirmSubmit = (e) => {
    e.preventDefault();
    if (saved) { promoteM.mutate(); return; }          // retry activation, don't re-create
    if (!confirmArmed) { setConfirmArmed(true); return; }
    saveM.mutate();
  };

  return (
    <div className="new-project-page">
      <Link to="/projects" className="back-link">← Projects</Link>
      <header className="page-header">
        <h1>New project</h1>
        <p className="muted">
          Paste a brand's website URL. Markaii reads the site, extracts positioning, audience, and voice with AI,
          and seeds a marketing plan. You confirm before anything goes live — nothing publishes from a draft.
        </p>
      </header>

      {!extracted && (
        <section className="account-section">
          <form
            className="analyze-form"
            onSubmit={(e) => {
              e.preventDefault();
              analyzeM.reset();
              analyzeM.mutate(url);
            }}
          >
            <label className="login-field">
              <span>Website URL</span>
              <input
                type="url" required value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://clilancers.com"
                disabled={analyzeM.isPending}
                autoFocus
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={analyzeM.isPending || !url}>
              {analyzeM.isPending ? 'Analyzing — this takes 10-20s…' : 'Analyze'}
            </button>
            {analyzeM.isError && (
              <div className="post-action-msg err">
                {analyzeM.error.body?.detail || analyzeM.error.message}
              </div>
            )}
            <p className="muted small">
              We fetch the page over HTTPS (no JavaScript rendering), strip nav/footer, send the visible text to the AI,
              and ask for a structured brand profile. Public URLs only — private IPs are refused. Rate limited to 10 analyses/hour.
            </p>
          </form>

          <div className="muted small" style={{ textAlign: 'center', margin: '1rem 0 0.75rem' }}>— or —</div>

          <label className="login-field">
            <span>No website? Upload a brand document (.pdf, .txt, .md — max 10 MB)</span>
            <input
              type="file"
              accept=".pdf,.txt,.md,text/plain,text/markdown,application/pdf"
              disabled={analyzeFileM.isPending || analyzeM.isPending}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) { analyzeFileM.reset(); analyzeFileM.mutate(f); }
                e.target.value = '';
              }}
            />
          </label>
          {analyzeFileM.isPending && (
            <p className="muted small">Analyzing the document — this takes 10-20s…</p>
          )}
          {analyzeFileM.isError && (
            <div className="post-action-msg err">
              {analyzeFileM.error.body?.detail || analyzeFileM.error.message}
            </div>
          )}
          <p className="muted small">
            A pitch deck export, brand guide, or even a plain-text description works — the same AI
            profile extraction runs on the document's text.
          </p>
        </section>
      )}

      {extracted && (
        <>
          <section className="account-section">
            <div className="extracted-banner">
              <span className="muted small">Source URL</span>
              <code>{sourceUrl}</code>
              <button type="button" className="btn-compact" onClick={() => { setExtracted(null); setSourceUrl(null); }}>
                ← Re-analyze
              </button>
            </div>
          </section>

          <section className="account-section">
            <h2>Review the AI extraction</h2>
            <p className="muted small">Edit anything you disagree with. Project is saved as a DRAFT — nothing fires until you click Save &amp; activate at the bottom.</p>
            {extracted?.low_confidence && (
              <div className="confirm-banner" role="alert">
                We couldn&apos;t read much from this site (it may be a JavaScript app that loads content after page load). These fields are rough guesses — please review and correct each one before activating.
              </div>
            )}

            <form className="project-form" onSubmit={onConfirmSubmit}>
              <label className="login-field">
                <span>Display name</span>
                <input type="text" required value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
              </label>
              <label className="login-field">
                <span>Project key (URL slug — can't change later)</span>
                <input type="text" required value={form.key} pattern="^[a-z][a-z0-9_-]{1,39}$"
                  onChange={(e) => setForm({ ...form, key: e.target.value })} />
                <small className="muted">lowercase letters / digits / dash / underscore, must start with a letter</small>
              </label>
              <label className="login-field">
                <span>Instagram handle</span>
                <input type="text" value={form.ig_handle}
                  onChange={(e) => setForm({ ...form, ig_handle: e.target.value })}
                  placeholder="@clilancers" />
              </label>
              <label className="login-field">
                <span>Positioning</span>
                <textarea required value={form.positioning} rows={2}
                  onChange={(e) => setForm({ ...form, positioning: e.target.value })} />
              </label>
              <label className="login-field">
                <span>Audience</span>
                <textarea required value={form.audience} rows={4}
                  onChange={(e) => setForm({ ...form, audience: e.target.value })} />
              </label>
              <label className="login-field">
                <span>Voice</span>
                <textarea required value={form.voice} rows={4}
                  onChange={(e) => setForm({ ...form, voice: e.target.value })} />
              </label>
              <div className="login-field">
                <span>Brand color <small className="muted">— used on generated slides &amp; accents</small></span>
                <BrandColorField value={form.brand_color}
                  onChange={(v) => setForm({ ...form, brand_color: v })} />
              </div>

              <div className="brief-divider" />
              <h3 className="wizard-subhead">Your vision &amp; goals <small className="muted">— optional, but this is what steers every post</small></h3>

              <label className="login-field">
                <span>Your vision <small className="muted">— the why, in your words</small></span>
                <textarea value={form.mission} rows={2}
                  onChange={(e) => setForm({ ...form, mission: e.target.value })}
                  placeholder="e.g. Be the freelancer's anti-late-payment ally — practical, never preachy." />
              </label>
              <label className="login-field">
                <span>Primary goal</span>
                <select value={form.primary_goal}
                  onChange={(e) => setForm({ ...form, primary_goal: e.target.value })}>
                  {GOAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <label className="login-field">
                <span>Push right now <small className="muted">— a launch, season, or feature</small></span>
                <textarea value={form.current_focus} rows={2}
                  onChange={(e) => setForm({ ...form, current_focus: e.target.value })}
                  placeholder="e.g. Push the new automated-reminders feature this month." />
              </label>
              <label className="login-field">
                <span>Always / never rules <small className="muted">— applied to every post</small></span>
                <textarea value={form.custom_instructions} rows={3}
                  onChange={(e) => setForm({ ...form, custom_instructions: e.target.value })}
                  placeholder={'e.g.\nAlways mention the 14-day free trial when relevant.\nNever use more than one emoji.'} />
              </label>
              <div className="login-field">
                <span>Lean into these topics <small className="muted">— Enter to add</small></span>
                <ChipInput value={form.topics_emphasize}
                  onChange={(v) => setForm({ ...form, topics_emphasize: v })}
                  placeholder="e.g. late-payment psychology" />
              </div>
              <div className="login-field">
                <span>Stay away from <small className="muted">— Enter to add</small></span>
                <ChipInput value={form.topics_avoid}
                  onChange={(v) => setForm({ ...form, topics_avoid: v })}
                  placeholder="e.g. generic “hustle” motivation" />
              </div>

              <div className="brief-divider" />

              <label className="login-field">
                <span>Features (one per line)</span>
                <textarea value={form.features} rows={5}
                  onChange={(e) => setForm({ ...form, features: e.target.value })} />
              </label>
              <label className="login-field">
                <span>Hard constraints — “never say / always say” (one per line)</span>
                <textarea value={form.hard_constraints} rows={4}
                  onChange={(e) => setForm({ ...form, hard_constraints: e.target.value })} />
              </label>
              <div className="login-field">
                <span>Notification emails <small className="muted">— who gets approval &amp; digest emails</small></span>
                <EmailRecipients value={form.recipient_emails}
                  onChange={(v) => setForm({ ...form, recipient_emails: v })} />
              </div>

              {saveM.isError && <div className="post-action-msg err">{saveM.error?.body?.detail ?? saveM.error?.body?.error ?? saveM.error?.message ?? 'Save failed — please try again.'}</div>}
              {promoteM.isError && <div className="post-action-msg err">{promoteM.error?.body?.detail ?? promoteM.error?.body?.error ?? promoteM.error?.message ?? 'Activation failed — click Retry activation.'}</div>}

              {confirmArmed && !saved && (
                <div className="confirm-banner" role="alert">
                  This enables the 5 content pillars and generates Plan v1 for <strong>{form.display_name || 'this project'}</strong>. You approve every post before anything publishes.
                </div>
              )}

              <div className="form-actions">
                <Link to="/projects" className="btn-compact">Cancel</Link>
                {confirmArmed && !saved && !submitting && (
                  <button type="button" className="btn-compact" onClick={() => setConfirmArmed(false)}>Back</button>
                )}
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {saveM.isPending ? 'Saving draft…'
                    : promoteM.isPending ? 'Activating + generating plan…'
                    : saved ? 'Retry activation'
                    : confirmArmed ? 'Yes — create & activate'
                    : 'Save & activate'}
                </button>
              </div>
            </form>
          </section>
        </>
      )}
    </div>
  );
}

function slugify(s) {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40) || 'project';
}
